<template>
  <div v-if="isPending">
    <RevLoadingScreen :text="i18n(translations.loadingDetails)" />
  </div>

  <div v-else class="h-full">
    <div
      v-if="showForm"
      class="flex h-full flex-col"
      data-test="bank-details-form"
    >
      <p v-if="!isBmAgent">
        {{ i18n(translations.description) }}
      </p>
      <template v-if="step === 'choice'">
        <p class="body-1-bold my-24">
          {{ i18n(translations.bankTypedescription) }}
        </p>
        <div class="flex flex-col gap-16">
          <RevRadio
            v-for="option in bankTypes"
            :id="option.id"
            :key="option.id"
            v-model="radioId"
            :value="option.id"
            variant="full"
          >
            <template #label>{{ option.label }}</template>
          </RevRadio>
        </div>
      </template>
      <template v-if="step === 'details'">
        <FormGenerator
          v-if="step === 'details'"
          :class="{ 'mt-32': !isBmAgent }"
          :form-config
          :form-id
          :has-submit-button="false"
          @submit="handleSubmit"
        />

        <small
          v-if="buybackConfig.hasGdprLegalMentions"
          class="caption mt-24 flex-auto content-end"
        >
          <FormattedMessage :definition="translations.legalText">
            <template #link>
              <RevLink target="_blank" :to="legalLink">
                {{ i18n(translations.legalLinkText) }}
              </RevLink>
            </template>
          </FormattedMessage>
        </small>
      </template>
    </div>

    <div v-else>
      <p>{{ previewDescription }}</p>
      <RevList :hasExternalBorders="false">
        <RevListItemInteractive
          v-for="[label, description] in validApiValues"
          :key="label"
        >
          <template #label>
            {{ i18n(translations[label as keyof typeof translations]) }}
          </template>
          <template #description>
            {{ description }}
          </template>
        </RevListItemInteractive>
      </RevList>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import {
  type BankAccountPurposeType,
  type BankDetailsResponse,
  PAYMENT_INFOS_OPTIONS,
  type PaymentInfo,
  type PaymentInfosOptionsType,
} from '@backmarket/http-api/src/api-specs-buyback/customer/client'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { RevLink } from '@ds/components/Link'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevRadio } from '@ds/components/Radio'

import type {
  FormValues,
  Input,
} from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.types'
import FormGenerator from '@backmarket/nuxt-layer-buyback/components/FormGenerator/FormGenerator.vue'
import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/composables/config/useBuybackConfig'
import { generateAchForm } from '@backmarket/nuxt-layer-buyback/utils/generators/generateAchForm'
import { generateBacsForm } from '@backmarket/nuxt-layer-buyback/utils/generators/generateBacsForm'
import { generateIbanForm } from '@backmarket/nuxt-layer-buyback/utils/generators/generateIbanForm'

import translations from './BankDetails.translations'

function optionToBankType(option: PaymentInfosOptionsType | undefined) {
  switch (option) {
    case PAYMENT_INFOS_OPTIONS.BACS:
      return generateBacsForm().map((item) => ({
        ...item,
        cols: 6,
      }))
    case PAYMENT_INFOS_OPTIONS.ACH:
      return generateAchForm()
    case PAYMENT_INFOS_OPTIONS.SEPA:
    default:
      return generateIbanForm()
  }
}

const props = defineProps<{
  formId: string
  showForm: boolean
  bank: PaymentInfo | undefined
  isBmAgent?: boolean
  bankData: BankDetailsResponse | null
  isPending: boolean
  step: 'choice' | 'details'
}>()
const emit = defineEmits<{
  submit: [values: FormValues, accountType: BankAccountPurposeType]
}>()

const radioId = ref<BankAccountPurposeType>('CHECKING')

const i18n = useI18n()
const currentLocale = useI18nLocale()
const buybackConfig = useBuybackConfig()

const legalLink = `${currentLocale}/legal/data-protection`
const formConfig: Array<Input> = [...optionToBankType(props.bank?.options?.[0])]

const validApiValues = computed(() => {
  const { iban, routingNumber, accountNumber, sortCode } = props.bankData || {
    iban: null,
    accountNumber: null,
    routingNumber: null,
    sortCode: null,
  }

  return Object.entries({
    iban,
    routingNumber,
    accountNumber,
    sortCode,
  }).filter(([, val]) => Boolean(val))
})
const previewDescription = computed(() =>
  props.isBmAgent
    ? i18n(translations.agentPreviewDescription)
    : i18n(translations.previewDescription),
)

const bankTypes = computed(() => {
  return [
    {
      id: 'CHECKING',
      label: i18n(translations.typeChecking),
    },
    {
      id: 'SAVINGS',
      label: i18n(translations.typeSavings),
    },
  ]
})

function handleSubmit(values: FormValues) {
  emit('submit', values, radioId.value)
}
</script>
